import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../Assets/Images/Vireya_Insurance.png";
import downloadIcon from "../Assets/Images/download.png";

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  currentPage?: string;
}

export default function Sidebar({ isCollapsed = false, onToggle, currentPage }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track active quick navigation section
  const [activeQuickSection, setActiveQuickSection] = useState<string>("");

  useEffect(() => {
    if (currentPage === "Contact Information") {
      const sectionIds = ["personal-info", "residence-info", "prior-insurance", "communication-preferences"];
      const handleScroll = () => {
        const scrollPosition = window.scrollY + 120; // Offset for sticky header
        let activeId = sectionIds[0];
        for (const id of sectionIds) {
          const el = document.getElementById(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            const top = rect.top + window.scrollY;
            if (scrollPosition >= top) {
              activeId = id;
            }
          }
        }
        setActiveQuickSection(activeId);
      };
      window.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    }
    if (currentPage === "Driver Information") {
      // Find expanded driver card
      const expandedCard = Array.from(window.document.querySelectorAll(".driver-card-section")).find((card) => {
        const content = card.querySelector(".driver-card-content");
        return content && content.classList.contains("expanded");
      });
      if (!expandedCard) return;
      const driverId = expandedCard.getAttribute("data-driver-id");
      const sectionIds = [`personal-info-${driverId}`, `license-info-${driverId}`, `driving-history-${driverId}`, `sr22-info-${driverId}`];
      const handleScroll = () => {
        const scrollPosition = window.scrollY + 120;
        let activeId = sectionIds[0];
        for (const id of sectionIds) {
          const el = document.getElementById(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            const top = rect.top + window.scrollY;
            if (scrollPosition >= top) {
              activeId = id;
            }
          }
        }
        setActiveQuickSection(activeId);
      };
      window.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [currentPage]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if any workflow page is active
  const isWorkflowActive =
    location.pathname.includes("/insurance-workflow") ||
    location.pathname.includes("/contact-information") ||
    location.pathname.includes("/driver-information") ||
    location.pathname.includes("/vehicle-information") ||
    location.pathname.includes("/policy-coverage") ||
    location.pathname.includes("/review") ||
    location.pathname.includes("/processing-application") ||
    location.pathname.includes("/compare-quotes");

  // Check if user is admin
  const isAdminUser = localStorage.getItem("IsAdminUser") === "true";

  const menuItems = [
    {
      id: "dashboard",
      title: "Dashboard",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1" />
          <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1" />
          <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1" />
          <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" rx="1" />
        </svg>
      ),
      path: "/dashboard",
      active: location.pathname === "/dashboard",
    },
    {
      id: "search-client",
      title: "Search Client",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      path: "/search-client",
      active: location.pathname === "/search-client" || isWorkflowActive || location.pathname === "/select-quote-flow",
      onClick: () => {
        localStorage.removeItem("currentQuoteNumber");
      },
    },
   
    {
      id: "history",
      title: "History",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      path: "/history",
      active: location.pathname === "/history",
    },
    ...(isAdminUser ? [{
      id: "configuration",
      title: "Configuration",
      icon: (
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M19.4 12a7.4 7.4 0 0 0-.12-1l1.67-1.3a.5.5 0 0 0 .12-.66l-1.6-2.77a.5.5 0 0 0-.62-.22l-1.98.8a7.62 7.62 0 0 0-1.73-1l-.3-2.1a.5.5 0 0 0-.5-.42h-3.2a.5.5 0 0 0-.5.42l-.3 2.1a7.62 7.62 0 0 0-1.73 1l-1.98-.8a.5.5 0 0 0-.62.22L3.93 9.04a.5.5 0 0 0 .12.66l1.67 1.3a7.4 7.4 0 0 0 0 2l-1.67 1.3a.5.5 0 0 0-.12.66l1.6 2.77a.5.5 0 0 0 .62.22l1.98-.8c.53.42 1.11.77 1.73 1l.3 2.1a.5.5 0 0 0 .5.42h3.2a.5.5 0 0 0 .5-.42l.3-2.1c.62-.23 1.2-.58 1.73-1l1.98.8a.5.5 0 0 0 .62-.22l1.6-2.77a.5.5 0 0 0-.12-.66l-1.67-1.3c.08-.33.12-.66.12-1Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      path: "/configuration",
      active: location.pathname === "/configuration",
    }] : []),
    
  ];

  const handleNavigation = (path: string, menuItem?: any) => {
    // Call onClick handler if it exists
    if (menuItem?.onClick) {
      menuItem.onClick();
    }
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <aside className={`sidebar-container ${isCollapsed ? "sidebar-collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
      {/* Sidebar Header with Logo */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {!isCollapsed && !isMobile ? (
            <>
              <img src={logo} alt="Envela Insurance" className="sidebar-logo-img" />
              <div className="sidebar-brand">
                <h3>Agent Control Environment</h3>
              </div>
            </>
          ) : (
            <div className="sidebar-collapsed-icon">
              <img src={downloadIcon} alt="Download" className="collapsed-icon-img" />
            </div>
          )}
          <div className="sidebar-favicon">
            <img src={downloadIcon} alt="Download" className="favicon-icon-img" />
          </div>
        </div>

        <button className="sidebar-toggle" onClick={isMobile ? handleMobileToggle : onToggle}>
          {(isMobile ? mobileOpen : !isCollapsed) ? (
            // Close/Collapse Icon (X)
            <svg viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            // Open/Expand Icon (Hamburger)
            <svg viewBox="0 0 24 24" fill="none">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      </div>

      {/* Current Page Indicator */}
      {!isCollapsed && currentPage && (
        <div className="sidebar-current-page">
          <span>{currentPage}</span>
        </div>
      )}

      {/* User Info Section */}
      <div className="sidebar-user">
        <div className="sidebar-user-avatar">
          <span>{localStorage.getItem("AvatarName")}</span>
        </div>
        {!isCollapsed && (
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{localStorage.getItem("name")}</span>
            <span className="sidebar-user-role">Insurance Agent</span>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          {menuItems.map((item) => (
            <li key={item.id} className="sidebar-menu-item">
              <button className={`sidebar-menu-link ${item.active ? "active" : ""}`} onClick={() => handleNavigation(item.path, item)}>
                <span className="sidebar-menu-icon">{item.icon}</span>
                {!isCollapsed && <span className="sidebar-menu-text">{item.title}</span>}
              </button>
            </li>
          ))}
          {/* Quick Navigation: show only for ContactInformation or DriverInformation pages using currentPage prop */}
          {(currentPage === "Contact Information" ||
            currentPage === "Driver Information" ||
            // currentPage === "Vehicle Information" ||
            currentPage === "Review Details") && (
            <>
              <div className="sidebar-quick-nav-section">
                <div className="sidebar-nav-header">
                  <h2 className="sidebar-nav-title">Quick Navigation</h2>
                </div>
                <ul className="sidebar-menu">
                  {currentPage === "Contact Information" && (
                    <>
                      {[
                        { id: "personal-info", label: "Personal Information" },
                        { id: "residence-info", label: "Residence Information" },
                        { id: "prior-insurance", label: "Prior Insurance History" },
                        { id: "communication-preferences", label: "Communication Preferences" },
                      ].map((section) => (
                        <li className="sidebar-menu-item" key={section.id}>
                          <button
                            type="button"
                            className={`sidebar-menu-link${activeQuickSection === section.id ? " active" : ""}`}
                            onClick={() => {
                              document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                              setActiveQuickSection(section.id);
                            }}
                          >
                            <span className="sidebar-menu-text">{section.label}</span>
                          </button>
                        </li>
                      ))}
                    </>
                  )}
                  {currentPage === "Driver Information" &&
                    (() => {
                      const expandedCard = Array.from(window.document.querySelectorAll(".driver-card-section")).find((card) => {
                        const content = card.querySelector(".driver-card-content");
                        return content && content.classList.contains("expanded");
                      });
                      if (!expandedCard) return null;
                      const driverId = expandedCard.getAttribute("data-driver-id");
                      const sections = [
                        { id: `personal-info-${driverId}`, label: "Personal Information" },
                        { id: `license-info-${driverId}`, label: "License Information" },
                        { id: `driving-history-${driverId}`, label: "Driving History & Background" },
                        { id: `sr22-info-${driverId}`, label: "SR22 Information" },
                      ];
                      return (
                        <>
                          {sections.map((section) => (
                            <li className="sidebar-menu-item" key={section.id}>
                              <button
                                type="button"
                                className={`sidebar-menu-link${activeQuickSection === section.id ? " active" : ""}`}
                                onClick={() => {
                                  document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  setActiveQuickSection(section.id);
                                }}
                              >
                                <span className="sidebar-menu-text">{section.label}</span>
                              </button>
                            </li>
                          ))}
                        </>
                      );
                    })()}

                  {/* {currentPage === "Vehicle Information" &&
                    (() => {
                      const expandedCard = Array.from(window.document.querySelectorAll(".vehicle-card-section")).find((card) => {
                        const content = card.querySelector(".vehicle-card-content");
                        return content && content.classList.contains("expanded");
                      });
                      if (!expandedCard) return null;
                      const vehicleId = expandedCard.getAttribute("data-vehicle-id");
                      const sections = [
                        { id: `personal-info-${vehicleId}`, label: "Personal Information" },
                        { id: `license-info-${vehicleId}`, label: "License Information" },
                        { id: `driving-history-${vehicleId}`, label: "Driving History & Background" },
                        { id: `sr22-info-${vehicleId}`, label: "SR22 Information" },
                      ];
                      return (
                        <>
                          {sections.map((section) => (
                            <li className="sidebar-menu-item" key={section.id}>
                              <button
                                type="button"
                                className={`sidebar-menu-link${activeQuickSection === section.id ? " active" : ""}`}
                                onClick={() => {
                                  document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  setActiveQuickSection(section.id);
                                }}
                              >
                                <span className="sidebar-menu-text">{section.label}</span>
                              </button>
                            </li>
                          ))}
                        </>
                      );
                    })()} */}
                  {currentPage === "Review Details" && (
                    <>
                      {[
                        { id: "review-contact-info", label: "Contact Information" },
                        { id: "review-driver-info", label: "Driver Information" },
                        { id: "review-vehicle-info", label: "Vehicle Information" },
                        { id: "review-policy-coverage", label: "Policy Coverage" },
                      ].map((section) => (
                        <li className="sidebar-menu-item" key={section.id}>
                          <button
                            type="button"
                            className={`sidebar-menu-link${activeQuickSection === section.id ? " active" : ""}`}
                            onClick={() => {
                              document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                              setActiveQuickSection(section.id);
                            }}
                          >
                            <span className="sidebar-menu-text">{section.label}</span>
                          </button>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              </div>
            </>
          )}
        </ul>
      </nav>

      {/* Sidebar Footer */}
      <div className="sidebar-footer">
        {!isCollapsed ? (
          <div className="sidebar-footer-content">
            {/* <div className="sidebar-footer-info">
              <p className="sidebar-footer-text">Envela Insurance</p>
              <p className="sidebar-footer-version">v2.0.1</p>
            </div> */}
            <button className="sidebar-logout-btn" onClick={handleLogout} title="Logout">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        ) : (
          <button className="sidebar-logout-btn collapsed" onClick={handleLogout} title="Logout">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>
    </aside>
  );
}
